import React, { useMemo } from 'react';
import {
 Flex,
 Stack,
 Checkbox,
 Text,
 RadioGroup,
 Radio,
 Box,
 Button,
} from '@chakra-ui/react';
import { QuestionHelper } from '../Data';
import { InputText } from '../Input';
import { NumericFormat } from 'react-number-format';
import ChatAnswersPopover from '../Feedback/Chat';
 import RotuloClassificacao from '../Catalog/RotuloClassificacao';
 import RotuloGri from '../Catalog/RotuloGri';
 import MedidasGri from '../Catalog/MedidasGri';

const QuestionCardSCertificationType = React.memo(
 ({
  question,
  selectedCompanyId,
  handleQuestionEvidence,
  surveyId,
  userId,
  questionnaireType,
  themeId,
  ...rest
 }) => {
  const { code, description, type, helper } = question;

  const questionOptions = useMemo(() => {
   return question?.options
    ? question?.options
    : question?.question_option_template
    ? question?.question_option_template
    : [];
  }, [question]);

  const filteredResponse = useMemo(() => {
   if (Array.isArray(question?.users_responses)) {
    return question?.users_responses?.find(
     (response) => response?.company?.id_company === +selectedCompanyId
    );
   }

   return question?.user_responses || null;
  }, [question, selectedCompanyId]);

  const response = filteredResponse?.response || null;

  const selectedOptions = useMemo(() => {
   if (response && (type === 'multiple_choice' || type === 'multi_choice')) {
    let responseParts = [];
    let canBeOld = false;

    if (response?.includes('||')) {
     responseParts = response?.split('||').map((part) => part.trim());
    } else if (response?.includes(',') && !response?.includes('||')) {
     canBeOld = true;
     responseParts = response?.split(',').map((part) => part.trim());
    } else {
     responseParts = [response?.trim()];
    }

    let options = questionOptions.filter((option) =>
     responseParts.includes(option.description.trim())
    );

    if (options.length === 0 && canBeOld) {
     options = questionOptions.filter((option) => response?.includes(option.description));
    }

    return options;
   }
   return [];
  }, [response, type, questionOptions]);

  return (
   <Stack
    spacing={['16px', '6']}
    p='6'
    pb='26px'
    bg='white'
    borderRadius='12px'
    my='16px'
    border='2px solid #EAEEF0'
    pos='relative'
    {...rest}
   >
    <ChatAnswersPopover
     question={question}
     surveyId={surveyId}
     companyId={selectedCompanyId}
     type={questionnaireType}
     userId={userId}
     {...(themeId && { themeId })}
    />
    <Box maxW='86%'>
     {question?.is_required && (
      <Text
       as='span'
       color='orange.100'
       fontSize='12px'
       lineHeight='24px'
       fontWeight={500}
       display='block'
      >
       Pergunta obrigatória*
      </Text>
     )}
     <Text lineHeight='32px' mt='4px'>
      <b>{code}</b> -{' '}
      <Text
       as='span'
       fontWeight={500}
       dangerouslySetInnerHTML={{ __html: description }}
      />
     </Text>

     {/* EG-42: o rótulo "Pilar > Tema", como nos cards de edição e criação.
         Fica FORA do <Text> acima de propósito: aquele nó usa
         dangerouslySetInnerHTML e não aceita filhos. O endpoint que alimenta esta
         tela (survey/admin/pillar/answered/certificate) já mandava id_pillar e
         id_theme — faltava só exibir. */}
     <RotuloClassificacao
      idPillar={question?.id_pillar}
      idTheme={question?.id_theme}
      pillarName={question?.pillar_name}
      themeTitle={question?.theme_title}
     />

     {/* GRI-02 — CA02: mesmo rótulo do card de edição/criação do template
         (Question-card-certification.jsx), que faltava aqui. O endpoint
         admin/pillar/answered/certificate passou a mandar `gri` por questão
         junto com id_pillar/id_theme.

         `comRotulo` porque aqui o leitor é o avaliador abrindo a resposta de
         uma empresa: o código sozinho não avisa que aquela pergunta alimenta
         um indicador que vai para o gráfico e para a comparação de mercado.

         GRI-07: o selo FICA no admin. No form ele saiu (a seção de medida
         passou a ser o sinal), mas aqui quem lê é o avaliador, que precisa
         reconhecer a questão como indicador mesmo quando ela ainda não tem
         medida nenhuma para a seção mostrar. */}
     <RotuloGri gri={question?.gri} comRotulo />
    </Box>

    {helper && (
     <QuestionHelper
      description={helper?.description || ''}
      videoUrl={helper?.url_video}
     />
    )}

    {!filteredResponse && (
     <Text fontSize='sm' fontWeight={500} color='#EA582B' lineHeight='16.44px'>
      Pergunta ainda não respondida
     </Text>
    )}

    {filteredResponse && (
     <Box>
      {type === 'text' && (
       <Flex
        minHeight='48px'
        height='fit-content'
        color='black.100'
        bg='#F5F5FB'
        fontSize='sm'
        fontWeight={500}
        lineHeight='18px'
        border='1px solid #F5F5FB'
        borderRadius='8px'
        py='14px'
        px='16px'
       >
        <Text
         as='span'
         lineHeight='18px'
         fontWeight={500}
         dangerouslySetInnerHTML={{ __html: filteredResponse?.response }}
        />
       </Flex>
      )}

      {type === 'multi_choice' && (
       <RadioGroup defaultValue={selectedOptions[0]?.description || ''}>
        <Flex gap='12px' flexWrap='wrap' className='questionary-app'>
         {questionOptions?.map(
          (option, i) =>
           option?.description && (
            <Radio key={i} value={option?.description} isReadOnly>
             {option.description}
            </Radio>
           )
         )}
        </Flex>
       </RadioGroup>
      )}

      {type === 'multiple_choice' && (
       <Flex gap='24px' flexWrap='wrap'>
        {questionOptions?.map(
         (option, i) =>
          option?.description && (
           <Checkbox
            key={i}
            value={option?.description}
            isChecked={selectedOptions.some(
             (selected) => selected.description === option.description
            )}
            isReadOnly
            className='regular-checkbox'
           >
            <Text fontSize='sm' color='#5C6066'>
             {option.description}
             {(option?.isPrerequisite === 'Sim' ||
              option?.isPrerequisite === true ||
              option?.is_prerequisite === true) && (
              <Text as='span' color='#EA582B'>
               {' (pré-requisito)'}
              </Text>
             )}
            </Text>
           </Checkbox>
          )
        )}
       </Flex>
      )}

      {type === 'number' && (
       <InputText
        defaultValue={filteredResponse?.response}
        type='number'
        readOnly
        w='411px'
       />
      )}

      {type === 'money' && (
       <NumericFormat
        value={filteredResponse?.response}
        displayType='text'
        thousandSeparator=','
        decimalSeparator='.'
        prefix='R$ '
        decimalScale={3}
        className='number_input'
       />
      )}

      {type === 'percentage' && (
       <NumericFormat
        value={filteredResponse?.response}
        suffix='%'
        displayType='text'
        maxLength={4}
        fixedDecimalScale
        className='number_input'
       />
      )}

      {filteredResponse?.response_file?.length > 0 && (
       <Button
        variant='secondary'
        fontSize='15px'
        width='224px'
        height='49px'
        mt='20px'
        color='#0073BB'
        onClick={() => handleQuestionEvidence(question?.id_question, filteredResponse)}
       >
        Vizualizar evidências
       </Button>
      )}
     </Box>
    )}

    {/* GRI-07: fora do `filteredResponse && ...` de propósito — questão ainda
        sem resposta também tem indicador vinculado, e precisa mostrar isso
        (sempreVisivel lista pelo catálogo da questão, "—" pro que falta).
        Este card tinha ficado com o MedidasGri preso ao bloco de resposta
        (só o Question-card-simple.jsx tinha recebido o ajuste), por isso o
        certificado com pilares não mostrava indicador nenhum antes de a
        empresa responder. */}
    <MedidasGri
     gri={question?.gri}
     valores={filteredResponse?.gri_values}
     sempreVisivel
    />
   </Stack>
  );
 }
);

export default QuestionCardSCertificationType;
